import { useEffect, useMemo, useState } from 'react';
import { differenceInCalendarDays, endOfDay, format, startOfDay, subDays } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { UsageCreditBalance } from 'features/credits/components/usage-credit-balance';
import { UsageDashboard } from 'features/usage/components/usage-dashboard';
import { UsageHeader } from 'features/usage/components/usage-header';
import { UsageDateRangePicker } from 'features/usage/components/usage-date-range-picker';
import { UsageLoadingSkeleton } from 'features/usage/components/usage-loading-skeleton';
import { UsageOrgCredits } from 'features/usage/components/usage-org-credits';
import { UsagePersonalCredits } from 'features/usage/components/usage-personal-credits';
import { UsageRangeWarning } from 'features/usage/components/usage-range-warning';
import { UsageScopeTabs } from 'features/usage/components/usage-scope-tabs';
import { UsageStateCard } from 'features/usage/components/usage-state-card';
import {
  useWindowLimit,
  useSubscriptionUsage,
  useTokenUsage,
  useTokenUsageByOperation,
} from 'features/usage/hooks/use-usage';
import { TokenUsageScope } from 'features/usage/services/usage-service';
import { buildUsageViewModel } from 'features/usage/utils/build-usage-view-model';
import { seatUsageMeters } from 'features/usage/utils/seat-usage-meters';
import { useSeatPools } from 'features/subscription/hooks/use-seats';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import { useCommonStore } from 'state/store/common/useCommonStore';

const MAX_RANGE_DAYS = 365;
// Hidden for now: the organization top-up card repeats the Personal tab's Shared credits card.
const SHOW_TOP_UP_BALANCE_ON_PERSONAL_TAB = false;

export default function UsagePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const currentUserId = useCommonStore((state) => state.user?.id) ?? '';
  const canViewOrgUsage = isAdmin || isOrgAdmin;
  const requestedUserId = searchParams.get('userId') ?? '';
  const viewingUserId = canViewOrgUsage ? requestedUserId : '';
  const viewingUserName = (location.state as { userName?: string } | null)?.userName;
  const today = useMemo(() => new Date(), []);
  const initialRange = useMemo<DateRange>(
    () => ({
      from: subDays(today, 6),
      to: today,
    }),
    [today]
  );

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(initialRange);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [tab, setTab] = useState<'personal' | 'organization'>('personal');
  const showTabs = canViewOrgUsage && !viewingUserId;
  const scope: TokenUsageScope = viewingUserId
    ? 'user'
    : showTabs && tab === 'organization'
      ? 'org'
      : 'me';

  useEffect(() => {
    if (!isWarningVisible) return undefined;

    const timeoutId = window.setTimeout(() => {
      setIsWarningVisible(false);
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [isWarningVisible]);

  // Which meter the credits card reports: the sum of every seat (empty userId) on the org tab,
  // otherwise the seat of whoever is being viewed.
  const meterUserId = scope === 'org' ? '' : viewingUserId || currentUserId;

  const rangeStart = startOfDay(selectedRange?.from ?? initialRange.from ?? today);
  const rangeEnd = endOfDay(selectedRange?.to ?? selectedRange?.from ?? initialRange.to ?? today);
  const startDate = format(rangeStart, 'yyyy-MM-dd');
  const endDate = format(rangeEnd, 'yyyy-MM-dd');

  const usageParams = {
    startDate,
    endDate,
    scope,
    userId: viewingUserId || undefined,
  };
  const rangeEnabled = !!selectedRange?.from;
  const usageQuery = useTokenUsage(usageParams, rangeEnabled);
  const operationQuery = useTokenUsageByOperation(usageParams, rangeEnabled);
  const subscriptionUsageQuery = useSubscriptionUsage();
  const windowLimitQuery = useWindowLimit(meterUserId);
  const { data: seatPools, refetch: refetchSeatPools } = useSeatPools(canViewOrgUsage, true);
  const isOrganizationTab = scope === 'org';
  // The organization total is spent while the page stays open, so it is read again on every
  // switch to it rather than shown from the copy loaded with the page.
  useEffect(() => {
    if (isOrganizationTab) refetchSeatPools();
  }, [isOrganizationTab, refetchSeatPools]);
  // `mine` is what the signed-in person spends; an admin also sees every seat, which gives the
  // organization's total and any member they open.
  const creditMeters = useMemo(() => {
    const mine = (subscriptionUsageQuery.data ?? []).map((meter) => ({
      ...meter,
      userId: currentUserId,
    }));
    const seats = seatUsageMeters(seatPools ?? []).filter(
      (meter) => meter.userId !== currentUserId
    );
    return [...mine, ...seats];
  }, [subscriptionUsageQuery.data, seatPools, currentUserId]);

  const usageData = usageQuery.data;
  const viewModel = useMemo(() => (usageData ? buildUsageViewModel(usageData) : null), [usageData]);

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (range?.from && range.to) {
      const days = differenceInCalendarDays(range.to, range.from) + 1;
      if (days > MAX_RANGE_DAYS) {
        setIsWarningVisible(true);
        return;
      }
    }
    setSelectedRange(range);
  };

  // Figma spaces the header, tabs and cards 24px apart, tighter than the 40px default.
  return (
    <ConfigurationLayout className="gap-6">
      <UsageHeader
        viewingUserId={viewingUserId || undefined}
        viewingUserName={viewingUserName}
        canViewOrgUsage={canViewOrgUsage}
        onBack={() => navigate('/identity-management')}
      />
      {/* Figma puts the date picker on the tabs row; it stays right-aligned when there are no tabs. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {showTabs && <UsageScopeTabs tab={tab} onTabChange={setTab} />}
        <UsageDateRangePicker
          periodStart={usageData?.period.start}
          periodEnd={usageData?.period.end}
          selectedRange={selectedRange}
          initialFrom={initialRange.from}
          today={today}
          dayCount={viewModel?.periodDays.length ?? 0}
          onRangeSelect={handleRangeSelect}
          onResetRange={() => setSelectedRange(initialRange)}
        />
      </div>

      {/* The Personal tab's Shared credits card already shows this balance; flip the flag to show both. */}
      {scope === 'me' && SHOW_TOP_UP_BALANCE_ON_PERSONAL_TAB && (
        <div className="grid gap-x-4 gap-y-6 md:grid-cols-3">
          <UsageCreditBalance />
        </div>
      )}
      {/* Every plan's credits and the shared top-up credits, in one section. */}
      {scope === 'org' && <UsageOrgCredits />}

      {/* Your own usage and a member's page get the daily, monthly and shared credit cards. */}
      {(scope === 'me' || scope === 'user') && (
        <UsagePersonalCredits
          userId={meterUserId}
          isOwnUsage={scope === 'me'}
          meter={creditMeters.find(
            (meter) => meter.meterKey === 'ai-credits' && meter.userId === meterUserId
          )}
          windows={windowLimitQuery.data?.windows ?? []}
        />
      )}
      {isWarningVisible && <UsageRangeWarning onDismiss={() => setIsWarningVisible(false)} />}
      {usageQuery.isLoading && <UsageLoadingSkeleton showUsers={scope === 'org'} />}

      {usageQuery.isError && (
        <UsageStateCard
          icon={<AlertCircle className="h-5 w-5" />}
          title={t('DATA_UNAVAILABLE')}
          description={t('LOAD_FAILED')}
        />
      )}

      {!usageQuery.isLoading && !usageQuery.isError && !usageData && (
        <UsageStateCard
          icon={<AlertCircle className="h-5 w-5" />}
          title={t('NO_DATA')}
          description={t('NO_DATA_FOR_RANGE')}
        />
      )}

      {!usageQuery.isLoading && !usageQuery.isError && usageData && viewModel && (
        <UsageDashboard
          data={usageData}
          viewModel={viewModel}
          operations={operationQuery.data ?? []}
          showUsers={scope === 'org'}
          isOwnUsage={scope === 'me'}
          isAdmin={canViewOrgUsage}
        />
      )}
    </ConfigurationLayout>
  );
}
